import { SITE_CONFIG } from '@maria-cavalheiro-website/shared-types/config'
import type { IconName } from '@maria-cavalheiro-website/shared-ui-web'

/**
 * Outbound share links, built once at module scope.
 *
 * Pure strings: no state, no effects, nothing to recompute. These are plain
 * navigations, not fetches, so `connect-src 'self'` is untouched — and must stay
 * untouched. There is no form here and there must not be one; Netlify Forms is
 * forbidden outright.
 *
 * NOTE ON THE ORIGIN: these follow `SITE_CONFIG.origin`. Until that is repointed
 * at the production domain, they carry the Netlify placeholder — which is correct
 * behaviour, not a bug. Hardcoding the production domain here to get ahead of the
 * repoint would create a second source of truth.
 */

export interface ShareLink {
  readonly network: string
  readonly href: string
  readonly icon: IconName
  /** Visually hidden, so the visible label remains the start of the accessible name. */
  readonly action: string
}

export const SHARE_URL = `${SITE_CONFIG.origin}/`
export const SHARE_TEXT = `Poemas de ${SITE_CONFIG.author}`
export const SHARE_TITLE = SITE_CONFIG.name

const encodedUrl = encodeURIComponent(SHARE_URL)
const encodedText = encodeURIComponent(SHARE_TEXT)

/**
 * The verb lives in visually hidden text rather than an `aria-label`.
 *
 * § 2.5.3 Label in Name requires the accessible name to contain the visible label.
 * The design used `aria-label="Compartilhar no Facebook"` over the visible word
 * "Facebook", which passes only by accident of substring order; making the visible
 * text the start of the name is the robust form, and it keeps voice control
 * working ("click Facebook").
 */
export const SHARE_LINKS: readonly ShareLink[] = Object.freeze([
  {
    network: 'Facebook',
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    icon: 'facebook',
    action: 'Compartilhar no',
  },
  {
    network: 'X',
    href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    icon: 'x',
    action: 'Compartilhar no',
  },
  {
    network: 'LinkedIn',
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    icon: 'linkedin',
    action: 'Compartilhar no',
  },
])
